import { Download, CalendarRange } from "lucide-react";
import {
    BarChart, Bar, AreaChart, Area, XAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { AppChrome } from "./DashboardMockup";

const sourceData = [
    { s: "Website", v: 680, color: "#16A34A" },
    { s: "Ads", v: 540, color: "#3B82F6" },
    { s: "Referral", v: 320, color: "#F59E0B" },
    { s: "IndiaMart", v: 410, color: "#8B5CF6" },
    { s: "LinkedIn", v: 260, color: "#0EA5E9" },
    { s: "Field", v: 330, color: "#64748B" },
];

const revenueData = [
    { m: "Feb", v: 18 }, { m: "Mar", v: 24 }, { m: "Apr", v: 21 },
    { m: "May", v: 32 }, { m: "Jun", v: 38 }, { m: "Jul", v: 47 },
];

const funnel = [
    ["Leads", 2540, 100],
    ["Qualified", 980, 62],
    ["Proposal", 410, 38],
    ["Negotiation", 220, 24],
    ["Won", 165, 16],
];

const performers = [
    ["Rahul Sharma", "₹21.4L won", 94],
    ["Priya Nair", "₹18.9L won", 86],
    ["Arun Thomas", "₹14.2L won", 71],
];

const Card = ({ title, sub, children, className = "" }) => (
    <div className={`rounded-xl border border-[#EDF0F3] bg-white p-3 shadow-sm ${className}`}>
        <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-bold text-[#111827]">{title}</p>
            {sub && <span className="text-[8px] font-semibold text-[#94A3B8]">{sub}</span>}
        </div>
        {children}
    </div>
);

const ReportsScreen = () => (
    <div>
        <AppChrome active="Reports" />
        <div className="bg-[#F7F9FA] p-3.5 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="font-display text-[15px] font-extrabold text-[#111827]">Reports & Analytics</p>
                    <p className="text-[10px] text-[#64748B]">Performance across leads, pipeline and revenue</p>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="flex items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-2 py-1 text-[9px] font-semibold text-[#475569]">
                        <CalendarRange className="h-2.5 w-2.5" /> Last 6 months
                    </span>
                    <span className="flex items-center gap-1 rounded-lg bg-[#16A34A] px-2 py-1 text-[9px] font-bold text-white">
                        <Download className="h-2.5 w-2.5" /> Export
                    </span>
                </div>
            </div>

            <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
                <Card title="Leads by Source" sub="2,540 total">
                    <div className="h-[110px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sourceData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                                <XAxis dataKey="s" tick={{ fontSize: 8, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                                <Tooltip cursor={{ fill: "#F0FDF4" }} contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                                <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                                    {sourceData.map((d) => <Cell key={d.s} fill={d.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card title="Revenue Won" sub="₹ in lakhs">
                    <div className="h-[110px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="tourRev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#16A34A" stopOpacity={0.28} />
                                        <stop offset="100%" stopColor="#16A34A" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="m" tick={{ fontSize: 8, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #E5E7EB" }} formatter={(v) => [`₹${v}L`, "Won"]} />
                                <Area type="monotone" dataKey="v" stroke="#16A34A" strokeWidth={2} fill="url(#tourRev)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card title="Conversion Funnel" sub="This quarter">
                    <div className="grid gap-1.5">
                        {funnel.map(([label, count, pct]) => (
                            <div key={label} className="flex items-center gap-2">
                                <span className="w-16 shrink-0 text-[8px] font-semibold text-[#64748B]">{label}</span>
                                <span className="h-3.5 rounded-md bg-gradient-to-r from-[#16A34A] to-[#4ADE80]" style={{ width: `${pct}%` }} />
                                <span className="shrink-0 text-[8px] font-bold text-[#111827]">{count.toLocaleString("en-IN")}</span>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card title="Top Performers" sub="By revenue won">
                    <div className="grid gap-2">
                        {performers.map(([name, stat, pct], i) => (
                            <div key={name} className="flex items-center gap-2">
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#111827] text-[7px] font-bold text-white">
                                    {name.split(" ").map((n) => n[0]).join("")}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between">
                                        <span className="truncate text-[9px] font-semibold text-[#111827]">{name}</span>
                                        <span className="text-[8px] font-bold text-[#16A34A]">{stat}</span>
                                    </div>
                                    <span className="mt-0.5 block h-1 rounded-full bg-[#F1F5F9]">
                                        <span className="block h-1 rounded-full bg-[#16A34A]" style={{ width: `${pct}%` }} />
                                    </span>
                                </div>
                                <span className="w-3 text-[8px] font-bold text-[#94A3B8]">#{i + 1}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    </div>
);

export default ReportsScreen;
